import { holdPropsInUtc, PushNotification } from "@repo/helpers";
import {
	FilterItem,
	KeyedArray,
	keyPair,
	RepFieldAudit,
	RepFieldAuditHistory,
	RepFieldAuditQuestionOption,
} from "@repo/models";
import { useCallback } from "react";

import { useLocationContext } from "../contexts/editableItemContext";
import { useLocationPortalApi, useQueryWrapper } from "./locationPortalApi";
import * as Queries from "./queries";

export function useRepFieldAuditApi() {
	const { Get, Post } = useLocationPortalApi("LocationPortal");

	const saveFieldAudit = useCallback(
		function (repFieldAudit: RepFieldAudit, email: boolean) {
			const fieldAudit = {
				...repFieldAudit,
				questionGroups: undefined,
				responseMap: undefined,
			};

			return Post<number>("SaveFieldAudit", { email }, fieldAudit);
		},
		[Post],
	);

	const getFieldAuditQuestionOptions = useCallback(
		function () {
			return Get<Map<string, RepFieldAuditQuestionOption>>("GetFieldAuditQuestionOptions");
		},
		[Get],
	);

	const getFieldAuditComplaintTypes = useCallback(
		function () {
			return Get<keyPair[]>("GetFieldAuditComplaintTypes");
		},
		[Get],
	);

	const getLocations = useCallback(
		function (locationId: number) {
			return Get<keyPair[]>("GetLocations", { locationId });
		},
		[Get],
	);

	const getFormTypes = useCallback(
		function (locationId: number) {
			return Get<keyPair<FilterItem>[]>("GetFormTypes", { locationId });
		},
		[Get],
	);

	const initialize = useCallback(function (audit: RepFieldAudit) {
		if (!audit) return audit;
		holdPropsInUtc(audit, "createdOn", "auditDate");
		audit.time ||= null;
		return audit;
	}, []);

	const getRepFieldAudits = useCallback(
		async function (locationId: number) {
			const fieldAudits = await Get<RepFieldAudit[]>("FieldAudits", { locationId });
			return fieldAudits.map(initialize);
		},
		[Get, initialize],
	);

	const getFieldAuditQuestions = useCallback(
		async function (locationId: number, entityId: number, formType: number) {
			return Get<RepFieldAudit>("GetFieldAuditQuestions", {
				locationId,
				entityId,
				formType,
			}).then(initialize);
		},
		[Get, initialize],
	);

	const loadRepFieldAudits = useCallback(
		function (locationId: number) {
			return async function () {
				if (!locationId) return defaultArr();
				try {
					const audits = await getRepFieldAudits(locationId);
					const keyedItems = new KeyedArray<RepFieldAudit>(audits, (b) => b.representativeFieldAuditId);
					return keyedItems;
				} catch (e) {
					PushNotification("Unable to retrieve field audits");
					return defaultArr();
				}
			};
		},
		[getRepFieldAudits],
	);

	const getRepFieldAuditHistories = useCallback(
		function (fieldAuditId: number) {
			return Get<RepFieldAuditHistory[]>("GetFieldAuditHistories", { fieldAuditId });
		},
		[Get],
	);

	const getAllFieldAuditHistories = useCallback(
		function (locationId: number) {
			return Get<RepFieldAuditHistory[]>("GetAllFieldAuditHistories", { locationId });
		},
		[Get],
	);

	return {
		getRepFieldAudits,
		getFieldAuditQuestions,
		saveFieldAudit,
		getFieldAuditQuestionOptions,
		getFieldAuditComplaintTypes,
		getLocations,
		getFormTypes,
		loadRepFieldAudits,
		getRepFieldAuditHistories,
		getAllFieldAuditHistories,
	};
}

export function useRepFieldAuditQueries() {
	const { locationId } = useLocationContext();

	const {
		loadRepFieldAudits,
		getFormTypes,
		getFieldAuditQuestionOptions,
		getFieldAuditComplaintTypes,
		getFieldAuditQuestions,
		getLocations,
		getRepFieldAuditHistories,
		getAllFieldAuditHistories,
	} = useRepFieldAuditApi();

	const useRepFieldAuditQuery = () =>
		useQueryWrapper(Queries.REP_FIELD_AUDITS, [locationId, loadRepFieldAudits], loadRepFieldAudits(locationId));

	const useFormTypesQuery = () =>
		useQueryWrapper(Queries.REP_FIELD_AUDIT_FORM_TYPES, [locationId, getFormTypes], () => getFormTypes(locationId));

	const useQuestionsQuery = (fieldAuditId: number, formTypeId: number) =>
		useQueryWrapper(
			Queries.REP_FIELD_AUDIT_QUESTIONS,
			[locationId, fieldAuditId, formTypeId, getFieldAuditQuestions],
			() => getFieldAuditQuestions(locationId, fieldAuditId, formTypeId),
		);

	const useQuestionOptionsQuery = () =>
		useQueryWrapper(Queries.REP_FIELD_AUDIT_QUESTION_OPTIONS, [locationId, getFieldAuditQuestionOptions], () =>
			getFieldAuditQuestionOptions(),
		);

	const useComplaintTypesQuery = () =>
		useQueryWrapper(Queries.REP_FIELD_AUDIT_COMPLAINT_TYPES, [locationId, getFieldAuditComplaintTypes], () =>
			getFieldAuditComplaintTypes(),
		);

	const useLocationsQuery = () =>
		useQueryWrapper(Queries.REP_FIELD_AUDIT_LOCATIONS, [locationId, getLocations], () => getLocations(locationId));

	const useFieldAuditHistories = (fieldAuditId: number) =>
		useQueryWrapper(
			Queries.REP_FIELD_AUDIT_LOCATIONS,
			[fieldAuditId, getRepFieldAuditHistories],
			() => getRepFieldAuditHistories(fieldAuditId),
			{ enabled: !!fieldAuditId },
		);

	const useAllFieldAuditHistoriesQuery = () =>
		useQueryWrapper(
			Queries.REP_FIELD_AUDIT_ALL_HISTORIES,
			[locationId, getAllFieldAuditHistories],
			() => getAllFieldAuditHistories(locationId),
			{ enabled: !!locationId },
		);

	return {
		useRepFieldAuditQuery,
		useFormTypesQuery,
		useQuestionsQuery,
		useQuestionOptionsQuery,
		useComplaintTypesQuery,
		useLocationsQuery,
		useFieldAuditHistories,
		useAllFieldAuditHistoriesQuery,
	};
}

const defaultArr = () => new KeyedArray<RepFieldAudit>([], (b) => b.representativeFieldAuditId);
